import React, { useState } from 'react';
import moment from 'moment';
import { Stack } from '@mui/material';
import { useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { generatePersonaFullName } from 'utils/formatters';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { MUIBaseEntity, MUITable } from 'components/shared/MUITable';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { APIRequestPagination, APIRequestSort, PaginationResponse } from 'global';
import { createTableColumns } from './tableConfig';
import { CreditSubmissionTableFilters } from './CreditSubmissionTableFilters';
import { FetchProposalsWithPaginationVariables, GET_LIST_OF_PROPOSALS_BY_CREDIT_SUBMISSION } from '../../../api';

interface Props {
  accountId: string;
  creditSubmissionId: string;
}

interface CreditSubmissionsResponse {
  id: string;
  status: string;
  fullName: string;
  lastName: string;
  firstName: string;
  businessName: string;
  salesPersonName: string;
  createdDateTime: string;
  dynamicsAccountId: string | null;
}

export interface CreditSubmissionProposalEntity extends MUIBaseEntity {
  status: string;
  contactName: string;
  businessName: string;
  salesPersonName: string;
  createdDateTime: string;
  dynamicsAccountId: string | null;
}

export const CreditSubmissionProposalsTable = ({ accountId, creditSubmissionId }: Props) => {
  const history = useHistory();

  const form = useForm({
    defaultValues: {
      filters: {
        status: 'all',
        to: moment().endOf('year'),
        from: moment().startOf('year'),
      },
    },
  });

  const filters = useWatch({ control: form.control, name: 'filters' });

  const [pagination, setPagination] = useState<APIRequestPagination>({ page: 1, perPage: 15 });
  const [sorting, setSorting] = useState<APIRequestSort>({ key: 'created_date_time', value: 'desc' });

  const { data, loading } = useQuery<
    { proposals: PaginationResponse<CreditSubmissionsResponse> },
    { creditSubmissionId: string } & FetchProposalsWithPaginationVariables
  >(GET_LIST_OF_PROPOSALS_BY_CREDIT_SUBMISSION, {
    notifyOnNetworkStatusChange: false,
    variables: {
      creditSubmissionId,
      input: {
        accountId,
        pagination,
        sort: sorting,
        filters: {
          to: filters.to.toISOString(),
          from: filters.from.toISOString(),
          ...(filters.status !== 'all' ? { status: filters.status } : {}),
        },
      },
    },
  });

  const handleProposalRowClick = (id: string) => {
    history.push(`/proposalDetail/${id}`);
  };

  const handleRedirectToProposalPage = (id: string, dynamicsAccountId: string | null) => {
    window.open(generateProposalPageURL(id, dynamicsAccountId ?? ''), '_blank');
  };

  const columns = createTableColumns({ handleProposalRowClick, handleRedirectToProposalPage });

  return (
    <FormProvider {...form}>
      <Stack gap="0.5rem" height="100%">
        <CreditSubmissionTableFilters />

        <MUITable<CreditSubmissionProposalEntity>
          columns={columns}
          isLoading={loading}
          sorting={{ ...sorting, onChange: setSorting }}
          tableData={
            data?.proposals?.data?.map(proposal => ({
              id: proposal.id,
              status: proposal.status ?? 'draft',
              businessName: proposal.businessName,
              salesPersonName: proposal.salesPersonName,
              createdDateTime: proposal.createdDateTime,
              contactName: generatePersonaFullName(proposal),
              dynamicsAccountId: proposal.dynamicsAccountId,
            })) ?? []
          }
          pagination={{
            ...pagination,
            total: data?.proposals?.total ?? 0,
            onChange: nextValue => setPagination(prev => ({ ...prev, ...nextValue })),
          }}
        />
      </Stack>
    </FormProvider>
  );
};
